import type { z } from 'zod';
import type { SkillId } from '../ids.ts';
import type { itemSchema, recipeSchema } from '../schema.ts';

/**
 * Cooking: the Wharf's fish and the Chase's game made into meals at the Brewhouse kitchen. A meal
 * restores some Life and lifts Mood; meals share a short cooldown of their own, so they don't get
 * in the way of a draught or a feast. To add a dish, add a row.
 */

type Item = z.input<typeof itemSchema>;
type Recipe = z.input<typeof recipeSchema>;

/** Minutes before another meal can be eaten. */
const MEAL_COOLDOWN = 30;

const DISHES: {
  key: string;
  name: string;
  icon: string;
  /** The skill that caught it, and the level; Brewing (the kitchen) is needed two levels lower. */
  skill: SkillId;
  level: number;
  inputs: { item: string; quantity: number }[];
  portions: number;
  life: number;
  mood: number;
  value: number;
  description: string;
}[] = [
  { key: 'grilled-perch', name: 'Grilled perch', icon: 'fried-fish', skill: 'fishing', level: 1, inputs: [{ item: 'river-perch', quantity: 1 }], portions: 1, life: 0.08, mood: 10, value: 15, description: 'Butter, salt and a hot griddle.' },
  { key: 'rabbit-stew', name: 'Rabbit stew', icon: 'cooking-pot', skill: 'hunting', level: 1, inputs: [{ item: 'brace-of-rabbits', quantity: 1 }, { item: 'herbs', quantity: 1 }], portions: 2, life: 0.1, mood: 20, value: 18, description: 'Coney, leeks and thyme, simmered all afternoon.' },
  { key: 'pike-pie', name: 'Pike pie', icon: 'pie-slice', skill: 'fishing', level: 4, inputs: [{ item: 'pike', quantity: 1 }, { item: 'barley', quantity: 1 }], portions: 1, life: 0.12, mood: 20, value: 45, description: 'Bones and all, under a thick crust.' },
  { key: 'roast-pheasant', name: 'Roast pheasant', icon: 'roast-chicken', skill: 'hunting', level: 4, inputs: [{ item: 'brace-of-pheasants', quantity: 1 }], portions: 2, life: 0.15, mood: 30, value: 23, description: 'Barded with bacon and roasted on the spit.' },
  { key: 'smoked-salmon', name: 'Smoked salmon', icon: 'fish-smoking', skill: 'fishing', level: 8, inputs: [{ item: 'salmon', quantity: 1 }], portions: 2, life: 0.15, mood: 30, value: 35, description: 'Cold-smoked over oak chips for three days.' },
  { key: 'venison-pie', name: 'Venison pie', icon: 'ham-shank', skill: 'hunting', level: 9, inputs: [{ item: 'venison-haunch', quantity: 1 }, { item: 'barley', quantity: 2 }], portions: 3, life: 0.25, mood: 50, value: 43, description: 'Rich, dark and heavy with juniper.' },
  { key: 'cod-chowder', name: 'Cod chowder', icon: 'bowl-of-rice', skill: 'fishing', level: 12, inputs: [{ item: 'sea-cod', quantity: 1 }, { item: 'barley', quantity: 2 }], portions: 3, life: 0.2, mood: 40, value: 40, description: 'Cod, barley and cream: a sailor’s supper.' },
  { key: 'eel-pie', name: 'Conger eel pie', icon: 'eel', skill: 'fishing', level: 30, inputs: [{ item: 'conger-eel', quantity: 1 }, { item: 'barley', quantity: 2 }], portions: 4, life: 0.3, mood: 60, value: 95, description: 'Jellied, peppered and better than it sounds.' },
];

const items: Item[] = [];
const recipes: Recipe[] = [];
for (const d of DISHES) {
  items.push({
    key: d.key, name: d.name, icon: d.icon, kind: 'consumable', value: d.value, description: d.description,
    use: { lifeFraction: d.life, bars: { mood: d.mood }, cooldown: { group: 'meal', minutes: MEAL_COOLDOWN } },
  });
  recipes.push({
    key: d.key, name: d.name, group: 'Kitchen', venue: 'brewhouse',
    description: `${d.description} ${d.portions > 1 ? `${d.portions} portions.` : 'One portion.'}`,
    skills: [{ skill: d.skill, level: d.level }, { skill: 'brewing', level: Math.max(1, d.level - 2) }],
    cost: { bar: 'focus', amount: 3 + Math.ceil(d.level / 4) },
    inputs: d.inputs,
    product: { item: d.key, quantity: d.portions },
  });
}

export const kitchen = { items, recipes };
